import type { ViewSDK } from '@salesforce/platform-sdk/view';

import { useCallback } from 'react';

import { useViewSdk } from '../../../ViewSdkContext';
import { type ILookupOption, LookupObjectApiName } from '../../types';
import {
  type AccountFilters,
  type ContactFilters,
  type ISfAccount,
  type ISfContact,
  type ISfPaymentTerm,
  type ISfShipTo,
  type PaymentTermFilters,
  type ShipToFilters,
  toAccountOption,
  toContactOption,
  toPaymentTermOption,
  toShipToOption,
} from './lookupDefinitions';

interface LookupSearchRequestDetail {
  mode: 'autocomplete' | 'modal';
  objectApiName: LookupObjectApiName;
  requestId: string;
  variables: {
    accountId?: string;
    filters: Array<
      | Record<string, { eq: string } | { like: string }>
      | { or: Array<Record<string, { like: string }>> }
      | { RecordType: { Name: { eq: string } } }
    >;
    first: number;
  };
}

interface LookupSearchResponse<TRecord> {
  error?: string;
  records: TRecord[];
  requestId: string;
}

const LOOKUP_SEARCH_RESPONSE_EVENT = 'lookup-search-response';
const LOOKUP_RESPONSE_TIMEOUT_MS = 10_000;

/**
 * Asks the host LWC to run a lookup query and resolves with its raw, unmapped GraphQL records,
 * typed by the `TRecord` the caller declares for the object it asked for. Rejects if the host
 * reports an error or no response arrives within `LOOKUP_RESPONSE_TIMEOUT_MS`.
 *
 * @remarks
 * The host is a dumb query proxy: it runs the fixed GraphQL query for `objectApiName` with
 * the supplied GraphQL variables, and forwards back whatever nodes came back verbatim —
 * no field extraction, no wildcard-wrapping, no display shaping. Request shaping happens in
 * the search functions below; display mapping happens in `lookupDefinitions`.
 *
 * The guest has no authenticated path to Salesforce data of its own. `ViewSDK` is a DOM
 * `EventTarget`: `dispatchEvent` forwards the `lookup-search` event to the host, and the host's
 * matching `lookup-search-response` event (dispatched on the `lightning-ui-embedding` element)
 * arrives back through `addEventListener`. A `requestId` correlates the two since the event
 * stream isn't itself request-scoped. The listener is attached and the request is dispatched
 * inside the same Promise executor — in that order — so there's no gap where a fast host
 * response could arrive before anyone is listening for it.
 */
const requestLookupSearch = <TRecord>(
  viewSdk: ViewSDK,
  params: Omit<LookupSearchRequestDetail, 'requestId'>,
): Promise<TRecord[]> =>
  new Promise((resolve, reject) => {
    const requestId = crypto.randomUUID();
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleResponse = (event: Event) => {
      const response = (event as CustomEvent<LookupSearchResponse<TRecord>>).detail;
      if (response.requestId !== requestId) return;
      clearTimeout(timeoutId);
      viewSdk.removeEventListener?.(LOOKUP_SEARCH_RESPONSE_EVENT, handleResponse);
      if (response.error) reject(new Error(response.error));
      else resolve(response.records);
    };

    timeoutId = setTimeout(() => {
      viewSdk.removeEventListener?.(LOOKUP_SEARCH_RESPONSE_EVENT, handleResponse);
      reject(new Error(`Lookup search timed out for request ${requestId}`));
    }, LOOKUP_RESPONSE_TIMEOUT_MS);

    viewSdk.addEventListener?.(LOOKUP_SEARCH_RESPONSE_EVENT, handleResponse);

    const detail: LookupSearchRequestDetail = { ...params, requestId };
    viewSdk.dispatchEvent?.(new CustomEvent('lookup-search', { detail }));
  });

/** Account, Contact, and Ship To search functions, with the `ViewSDK` resolved from context. */
export const useLookupSearch = () => {
  const viewSdk = useViewSdk();

  const searchAccounts = async (term: string): Promise<ILookupOption[]> => {
    const records = await requestLookupSearch<ISfAccount>(viewSdk, {
      mode: 'autocomplete',
      objectApiName: LookupObjectApiName.Account,
      variables: { filters: [{ Name: { like: `%${term}%` } }], first: 10 },
    });
    return records.map(toAccountOption);
  };

  const searchKochParties = async (term: string): Promise<ILookupOption[]> => {
    const records = await requestLookupSearch<ISfAccount>(viewSdk, {
      mode: 'autocomplete',
      objectApiName: LookupObjectApiName.Account,
      variables: {
        filters: [
          { RecordType: { Name: { eq: 'Koch Party' } } },
          { O_Status__c: { eq: 'A' } },
          { Name: { like: `%${term}%` } },
        ],
        first: 5,
      },
    });
    return records.map(toAccountOption);
  };

  const searchContacts = async (term: string, accountId: string): Promise<ILookupOption[]> => {
    const records = await requestLookupSearch<ISfContact>(viewSdk, {
      mode: 'autocomplete',
      objectApiName: LookupObjectApiName.Contact,
      variables: {
        accountId,
        filters: [
          {
            or: [{ Email: { like: `%${term}%` } }, { Name: { like: `%${term}%` } }, { Phone: { like: `%${term}%` } }],
          },
        ],
        first: 10,
      },
    });
    return records.map(toContactOption);
  };

  const searchShipTos = async (term: string): Promise<ILookupOption[]> => {
    const records = await requestLookupSearch<ISfShipTo>(viewSdk, {
      mode: 'autocomplete',
      objectApiName: LookupObjectApiName.ShipTo,
      variables: { filters: [{ Name: { like: `%${term}%` } }], first: 10 },
    });
    return records.map(toShipToOption);
  };

  const searchPaymentTerms = async (term: string): Promise<ILookupOption[]> => {
    const records = await requestLookupSearch<ISfPaymentTerm>(viewSdk, {
      mode: 'autocomplete',
      objectApiName: LookupObjectApiName.PaymentTerm,
      variables: { filters: [{ Name: { like: `%${term}%` } }], first: 10 },
    });
    return records.map(toPaymentTermOption);
  };

  // SearchModal.tsx excludes blank/too-short fields before these searches. Account Type is a
  // picklist, so its GraphQL filter uses exact equality instead of the text fields' `like`.
  const searchAccountsModal = useCallback(
    (filters: AccountFilters, limit: number): Promise<ISfAccount[]> =>
      requestLookupSearch<ISfAccount>(viewSdk, {
        mode: 'modal',
        objectApiName: LookupObjectApiName.Account,
        variables: {
          filters: Object.entries(filters).map(([field, value]) => ({
            [field]: field === 'Type' ? { eq: value.trim() } : { like: `%${value.trim()}%` },
          })),
          first: limit,
        },
      }),
    [viewSdk],
  );

  const searchKochPartiesModal = useCallback(
    (filters: AccountFilters, limit: number): Promise<ISfAccount[]> =>
      requestLookupSearch<ISfAccount>(viewSdk, {
        mode: 'modal',
        objectApiName: LookupObjectApiName.Account,
        variables: {
          filters: [
            { RecordType: { Name: { eq: 'Koch Party' } } },
            { O_Status__c: { eq: 'A' } },
            ...Object.entries(filters).map(([field, value]) => ({
              [field]: field === 'Type' ? { eq: value.trim() } : { like: `%${value.trim()}%` },
            })),
          ],
          first: limit,
        },
      }),
    [viewSdk],
  );

  const searchContactsModal = useCallback(
    (filters: ContactFilters, limit: number, accountId: string): Promise<ISfContact[]> =>
      requestLookupSearch<ISfContact>(viewSdk, {
        mode: 'modal',
        objectApiName: LookupObjectApiName.Contact,
        variables: {
          accountId,
          filters: Object.entries(filters).map(([field, value]) => ({ [field]: { like: `%${value.trim()}%` } })),
          first: limit,
        },
      }),
    [viewSdk],
  );

  const searchShipTosModal = useCallback(
    (filters: ShipToFilters, limit: number): Promise<ISfShipTo[]> =>
      requestLookupSearch<ISfShipTo>(viewSdk, {
        mode: 'modal',
        objectApiName: LookupObjectApiName.ShipTo,
        variables: {
          filters: Object.entries(filters).map(([field, value]) => ({ [field]: { like: `%${value.trim()}%` } })),
          first: limit,
        },
      }),
    [viewSdk],
  );

  const searchPaymentTermsModal = useCallback(
    (filters: PaymentTermFilters, limit: number): Promise<ISfPaymentTerm[]> =>
      requestLookupSearch<ISfPaymentTerm>(viewSdk, {
        mode: 'modal',
        objectApiName: LookupObjectApiName.PaymentTerm,
        variables: {
          filters: Object.entries(filters).map(([field, value]) => ({ [field]: { like: `%${value.trim()}%` } })),
          first: limit,
        },
      }),
    [viewSdk],
  );

  return {
    searchAccounts,
    searchAccountsModal,
    searchContacts,
    searchContactsModal,
    searchKochParties,
    searchKochPartiesModal,
    searchPaymentTerms,
    searchPaymentTermsModal,
    searchShipTos,
    searchShipTosModal,
  };
};
